// src/components/TaskItem.js
import React, { useState, useRef, useEffect } from "react";
import { Store, MapPinned, CheckCircle2, Circle, Camera, Plus, ChevronDown, FileText, Sparkles, Pencil, Trash2 } from 'lucide-react';
import { updateTask, uploadTaskImage, addSubtasks } from '../services/taskService';
import { updateSubtask, deleteSubtask, generateBreakdown } from '../services/breakdownService';
import { getCategoryColor } from '../constants/categories';
import API_BASE_URL from '../config';
import './TaskItemCard.css'; // New styles



function TaskItem({ task, index, taskDate, onDelete, onTogglePriority, userLocation }) {
  const [taskText, setTaskText] = useState(task.text || task.task_text || "");
  const [description, setDescription] = useState(task.description || "");
  const [quantity, setQuantity] = useState(task.quantity || 1);
  const [productImage, setProductImage] = useState(
    task.image_url
      ? (task.image_url.startsWith('http') ? task.image_url : `${API_BASE_URL}${task.image_url}`)
      : null
  );
  const [selectedStore, setSelectedStore] = useState("");
  const [stores, setStores] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [storeOpen, setStoreOpen] = useState(false);
  // Details editor is collapsed by default (saves space); auto-open if the task
  // already has details to show.
  const [detailsOpen, setDetailsOpen] = useState(!!(task.description && task.description.trim()));
  const [editingTitle, setEditingTitle] = useState(false);
  const [subtasks, setSubtasks] = useState(task.subtasks || []);
  const [completed, setCompleted] = useState(!!task.completed);
  const [newSubtask, setNewSubtask] = useState('');
  const [breaking, setBreaking] = useState(false);
  const menuRef = useRef(null);
  const storeRef = useRef(null);
  const descRef = useRef(null);
  const photoInputRef = useRef(null);

  const toggleDetails = () => {
    setDetailsOpen((o) => {
      const next = !o;
      if (next) setTimeout(() => descRef.current?.focus(), 0);
      return next;
    });
  };

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (storeRef.current && !storeRef.current.contains(e.target)) setStoreOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Fetch stores and simulate prices
  React.useEffect(() => {
    if (!taskText) return;

    const fetchStores = async (lat, lng, locText) => {
      try {
        const locations = await require('../services/taskService').fetchNearbyStores(taskText, lat, lng, locText);
        const ONLINE_STORES = [
          { id: 'amzn', name: 'Amazon', distance: 'Online' },
          { id: 'ebay', name: 'eBay', distance: 'Online' }
        ];

        const adapted = locations.map((loc, i) => ({
          id: loc.name + i,
          name: loc.name,
          distance: loc.distance || 'Nearby',
          address: loc.address
        }));

        if (adapted.length > 0) setStores([...adapted, ...ONLINE_STORES]);
        else setFallbackStores();
      } catch (e) {
        console.error(e);
        setFallbackStores();
      }
    };

    if (userLocation?.lat && userLocation?.lng) {
      // Coordinates already resolved by the header (current location) — reuse
      // them so we don't re-prompt for geolocation here.
      fetchStores(userLocation.lat, userLocation.lng, null);
    } else if (userLocation?.type === 'custom') {
      fetchStores(null, null, userLocation.name);
    } else if (navigator.geolocation && userLocation?.type === 'current') {
      // Current location chosen but not yet resolved — ask the browser.
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          fetchStores(latitude, longitude, null);
        },
        () => setFallbackStores()
      );
    } else {
      setFallbackStores();
    }
  }, [taskText, userLocation]);

  const setFallbackStores = () => {
    setStores([
      { id: 'walmart', name: 'Walmart', distance: '1.2mi' },
      { id: 'target', name: 'Target', distance: '2.5mi' },
      { id: 'costco', name: 'Costco', distance: '3.0mi' },
      { id: 'wholefoods', name: 'Whole Foods', distance: '0.8mi' },
      { id: 'traderjoes', name: 'Trader Joe\'s', distance: '1.5mi' },
      { id: 'amzn', name: 'Amazon', distance: 'Online' },
      { id: 'ebay', name: 'eBay', distance: 'Online' }
    ]);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Optimistic preview
      const reader = new FileReader();
      reader.onloadend = () => setProductImage(reader.result);
      reader.readAsDataURL(file);

      // Upload to backend
      try {
        const response = await uploadTaskImage(task.id, file);
        if (response.task && response.task.image_url) {
          // Ensure we use the server URL after upload
          const url = response.task.image_url;
          setProductImage(url.startsWith('http') ? url : `${API_BASE_URL}${url}`);
        }
      } catch (err) {
        console.error("Failed to upload image", err);
        alert("❌ Failed to save image");
      }
    }
  };

  const saveChanges = async (overrides = {}) => {
    try {
      const payload = {
        ...task,
        text: overrides.taskText !== undefined ? overrides.taskText : taskText,
        description: overrides.description !== undefined ? overrides.description : description,
        quantity: overrides.quantity !== undefined ? overrides.quantity : quantity,
      };
      await updateTask(payload);
    } catch (e) {
      console.error("Failed to save task changes", e);
    }
  };

  const incrementQty = () => {
    const newQ = quantity + 1;
    setQuantity(newQ);
    saveChanges({ quantity: newQ });
  };

  const decrementQty = () => {
    const newQ = (quantity > 1 ? quantity - 1 : 1);
    setQuantity(newQ);
    saveChanges({ quantity: newQ });
  };

  const handleBlur = async () => {
    await saveChanges();
  };

  const handleBreakdown = async () => {
    setMenuOpen(false);
    setExpanded(true);
    if (!task.id) { window.alert('Save the task first, then break it down.'); return; }
    setBreaking(true);
    try {
      // Server-side break-down (the OpenAI key lives on the backend). Works for
      // every category; regenerates when the task already has subtasks.
      const res = await generateBreakdown(task.id, {
        cause: 'first_breakdown',
        force: subtasks.length > 0,
      });
      const next = res?.subtasks || [];
      if (!next.length) { window.alert('No subtasks generated. Try again.'); return; }
      setSubtasks(next);
    } catch (err) {
      const msg = err.response?.data?.error || 'Could not break down this task. Please try again.';
      window.alert(msg);
    } finally {
      setBreaking(false);
    }
  };

  // Mark the whole task done/undone. Optimistic toggle, persisted via the same
  // updateTask endpoint used for edits; revert if the save fails.
  const toggleCompleted = async () => {
    const next = !completed;
    setCompleted(next);
    try {
      await updateTask({ ...task, completed: next });
    } catch (e) {
      console.error('Failed to toggle completion', e);
      setCompleted(!next);
    }
  };

  const toggleSubtask = async (st) => {
    setSubtasks((prev) => prev.map((s) => (s.id === st.id ? { ...s, completed: !s.completed } : s)));
    try { await updateSubtask(st.id, { completed: !st.completed }); } catch (_) { /* revert on error not critical */ }
  };

  const removeSubtask = async (st) => {
    setSubtasks((prev) => prev.filter((s) => s.id !== st.id));
    try { await deleteSubtask(st.id); } catch (_) { /* ignore */ }
  };

  // Manually add a step to the checklist (no AI needed).
  const addManualSubtask = async () => {
    const text = newSubtask.trim();
    if (!text || !task.id) return;
    setNewSubtask('');
    try {
      const created = await addSubtasks(task.id, [text]);
      setSubtasks((prev) => [...prev, ...(created || [])]);
    } catch (_) {
      window.alert('Could not add the step. Please try again.');
    }
  };

  const getSearchQuery = () => {
    const parts = [taskText];
    if (description) parts.push(description);
    const store = stores.find(s => s.id === selectedStore);
    if (store && store.name) parts.push(store.name);
    return parts.join(' ').trim() || 'grocery store';
  };

  // Anchor the Google Maps search to the user's real location: center on their
  // coordinates when available, otherwise bias by the chosen city name.
  const getMapsHref = () => {
    const q = getSearchQuery();
    if (userLocation?.lat && userLocation?.lng) {
      return `https://www.google.com/maps/search/${encodeURIComponent(q)}/@${userLocation.lat},${userLocation.lng},14z`;
    }
    if (userLocation?.type === 'custom' && userLocation?.name) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${q} near ${userLocation.name}`)}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  };

  return (
    <div className={`task-card-item ${task.priority ? 'priority-task' : ''} ${expanded ? 'is-expanded' : ''}${completed ? ' is-completed' : ''}`}>

      {/* Shared photo input — triggered from both the Photo pill and the ⋮ menu
          (always present so it works even when the card is collapsed). */}
      <input ref={photoInputRef} type="file" accept="image/*" hidden onChange={handleImageUpload} />

      {/* Compact row — click anywhere (except controls) to expand/collapse */}
      <div className="task-row-head" onClick={() => setExpanded((v) => !v)}>
        <button
          className={`task-complete-btn${completed ? ' done' : ''}`}
          onClick={(e) => { e.stopPropagation(); toggleCompleted(); }}
          title={completed ? 'Mark not done' : 'Mark done'}
          aria-label="Toggle complete"
          style={completed ? { color: getCategoryColor(task.category) } : undefined}
        >
          {completed ? <CheckCircle2 size={20} /> : <Circle size={20} />}
        </button>
        <button
          className={`priority-star-btn ${task.priority ? 'active' : ''}`}
          onClick={(e) => { e.stopPropagation(); onTogglePriority(taskDate, index); }}
          title="Toggle Priority"
        >
          {task.priority ? '★' : '☆'}
        </button>

        {editingTitle ? (
          <input
            className="task-title-input"
            value={taskText}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setTaskText(e.target.value)}
            onBlur={() => { handleBlur(); setEditingTitle(false); }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
            placeholder="Task name (e.g. Buy Eggs)"
          />
        ) : (
          // Plain text so a click anywhere on the row toggles expand/collapse.
          // Rename via the ⋮ menu (which switches this into an input).
          <span className="task-title-text">{taskText || 'Untitled task'}</span>
        )}

        {quantity > 1 && <span className="task-qty-badge">×{quantity}</span>}
        {subtasks.length > 0 && <span className="task-qty-badge subtasks">{subtasks.filter((s) => s.completed).length}/{subtasks.length}</span>}

        {/* 3-dots options menu */}
        <div className="task-menu-wrap" ref={menuRef} onClick={(e) => e.stopPropagation()}>
          <button className="task-icon-btn" onClick={() => setMenuOpen((o) => !o)} title="Options" aria-label="Options">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" /></svg>
          </button>
          {menuOpen && (
            <div className="task-menu">
              <button onClick={() => { setMenuOpen(false); toggleCompleted(); }}><CheckCircle2 size={15} /> {completed ? 'Mark not done' : 'Mark done'}</button>
              <div className="task-menu-divider" />
              <button onClick={handleBreakdown}><Sparkles size={15} /> Breakdown with AI</button>
              <button onClick={() => { setMenuOpen(false); setExpanded(true); setEditingTitle(true); setDetailsOpen(true); }}><Pencil size={15} /> Edit</button>
              <div className="task-menu-divider" />
              <a
                className="task-menu-link"
                href={getMapsHref()}
                target="_blank" rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
              ><MapPinned size={15} /> Maps</a>
              <button onClick={() => { setMenuOpen(false); setExpanded(true); setStoreOpen(true); }}><Store size={15} /> Store</button>
              <button onClick={() => { setMenuOpen(false); photoInputRef.current?.click(); }}><Camera size={15} /> Photo</button>
              <button onClick={() => { setMenuOpen(false); setExpanded(true); setDetailsOpen(true); }}><FileText size={15} /> Details</button>
              <div className="task-menu-qty">
                <span className="task-menu-qty-label">Qty</span>
                <button className="task-menu-qty-btn" onClick={decrementQty}>−</button>
                <span className="task-menu-qty-val">{quantity}</span>
                <button className="task-menu-qty-btn" onClick={incrementQty}>+</button>
              </div>
              <div className="task-menu-divider" />
              <button className="danger" onClick={() => { setMenuOpen(false); onDelete(taskDate, index); }}><Trash2 size={15} /> Delete</button>
            </div>
          )}
        </div>

      </div>

      {/* Details — revealed on expand */}
      {expanded && (
        <div className="task-row-body">
          {detailsOpen && (
            <textarea
              ref={descRef}
              className="task-desc-input"
              placeholder="Add details — e.g. Organic large eggs, 12 count…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={handleBlur}
              rows={2}
            />
          )}

          {/* Footer actions */}
          <div className="task-actions-bar">
            <a
              href={getMapsHref()}
              target="_blank" rel="noopener noreferrer"
              className="card-action-link maps-link"
              title={`Search Maps for: ${getSearchQuery()}`}
            ><MapPinned size={15} strokeWidth={2} /> Maps</a>
            <div className="store-select" ref={storeRef}>
              <button
                className={`card-action-link store-link${selectedStore ? ' has-store' : ''}`}
                onClick={() => setStoreOpen((o) => !o)}
                title="Pick a store"
              >
                <Store size={15} strokeWidth={2} />
                <span className="store-link-label">
                  {stores.find((s) => s.id === selectedStore)?.name || 'Store'}
                </span>
                <ChevronDown size={13} className={`store-chev${storeOpen ? ' open' : ''}`} />
              </button>
              {storeOpen && (
                <div className="store-menu">
                  {stores.length === 0 ? (
                    <div className="store-menu-empty">No stores found nearby</div>
                  ) : (
                    stores.map((s) => (
                      <button
                        key={s.id}
                        className={`store-menu-item${selectedStore === s.id ? ' active' : ''}`}
                        onClick={() => { setSelectedStore(s.id); setStoreOpen(false); }}
                      >
                        <span className="store-menu-name">{s.name}</span>
                        {s.distance && <span className="store-menu-dist">{s.distance}</span>}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              className="card-action-link photo-link"
              title={productImage ? 'Change photo' : 'Add a photo'}
              onClick={() => photoInputRef.current?.click()}
            >
              {productImage
                ? <img className="photo-link-thumb" src={productImage} alt="" />
                : <Camera size={15} strokeWidth={2} />}
              Photo
            </button>
            <button
              className={`card-action-link details-link${description.trim() ? ' has-details' : ''}${detailsOpen ? ' open' : ''}`}
              onClick={toggleDetails}
              title="Add details"
            ><FileText size={15} strokeWidth={2} /> Details</button>

            <div className="quantity-control">
              <span className="qty-label">Qty</span>
              <button className="qty-btn" onClick={decrementQty}>-</button>
              <span className="qty-val">{quantity}</span>
              <button className="qty-btn" onClick={incrementQty}>+</button>
            </div>

            <button className="breakdown-ai-btn" onClick={handleBreakdown} disabled={breaking}>
              <Sparkles size={15} strokeWidth={2} /> {breaking ? 'Breaking down…' : (subtasks.length ? 'Regenerate with AI' : 'Breakdown with AI')}
            </button>
          </div>

          {/* Breakdown subtasks — status-icon checklist */}
          {subtasks.length > 0 && (
            <ul className="task-subtasks">
              {subtasks.map((st, i) => (
                <li
                  key={st.id}
                  className={`task-subtask${st.completed ? ' done' : ''}`}
                  style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
                >
                  <button className="task-subtask-check" onClick={() => toggleSubtask(st)} aria-label="Toggle done">
                    {st.completed ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                  </button>
                  <span className="task-subtask-text">{st.text}</span>
                  <button className="task-subtask-del" onClick={() => removeSubtask(st)} aria-label="Remove">×</button>
                </li>
              ))}
            </ul>
          )}

          {/* Manually add a step */}
          <div className="task-subtask-add">
            <span className="task-subtask-add-icon"><Plus size={16} /></span>
            <input
              className="task-subtask-add-input"
              placeholder="Add a step…"
              value={newSubtask}
              onChange={(e) => setNewSubtask(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addManualSubtask(); }}
            />
          </div>
        </div>
      )}

    </div>
  );
}

export default TaskItem;
